import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { NavHeader } from "@/components/layout/nav-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, Calendar as CalendarIcon } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useBookingsDate } from "@/hooks/use-bookings-date";
import { VenueTimeFormats, parseVenueDateTime } from "@/utils/timezone";
import { useBlockedDates } from "@/hooks/use-blocked-dates";
import { useBlockedTimeSlots } from "@/hooks/use-blocked-time-slots";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TIME_SLOTS_CONFIG } from "@/lib/constants";

// Schema for edit booking - all fields optional except date/time
const editBookingSchema = z.object({
  date: z.date({
    required_error: "Please select a date",
  }),
  timeSlot: z.string().min(1, "Please select a time slot"),
  duration: z.enum(["60", "90"]),
  description: z.string().optional(),
  causeLinkType: z.enum(["gofundme", "custom", "none"]),
  selectedGofundmeId: z.number().optional(),
  causeName: z.string().optional(),
  causeLink: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  causeInstagramUrl: z.string().optional(),
}).superRefine((data, ctx) => {
  // Validate cause link based on selection
  if (data.causeLinkType === "custom") {
    if (!data.causeLink) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide a custom cause link",
        path: ["causeLink"],
      });
    }
    if (!data.causeName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide a cause name",
        path: ["causeName"],
      });
    }
  } else if (data.causeLinkType === "gofundme" && !data.selectedGofundmeId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please select a GoFundMe link",
      path: ["selectedGofundmeId"],
    });
  }
});

type EditBookingFormValues = z.infer<typeof editBookingSchema>;

// Generate time slots
const generateTimeSlots = (duration: 60 | 90) => {
  const slots: string[] = [];
  const startHour = TIME_SLOTS_CONFIG.START_HOUR;
  const endHour = TIME_SLOTS_CONFIG.END_HOUR;

  if (duration === 90) {
    for (let hour = startHour; hour < endHour; hour++) {
      slots.push(`${String(hour).padStart(2, "0")}:00`);
      if (hour < endHour - 1) {
        slots.push(`${String(hour).padStart(2, "0")}:30`);
      }
    }
    return slots;
  }

  for (let hour = startHour; hour < endHour; hour++) {
    slots.push(`${hour.toString().padStart(2, "0")}:00`);
    if (hour < endHour - 1) {
      slots.push(`${hour.toString().padStart(2, "0")}:30`);
    }
  }
  return slots;
};

export default function EditBookingPage() {
  const { bookingId } = useParams();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Fetch booking details
  const { data: booking, isLoading: isLoadingBooking } = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: async () => {
      const res = await fetch(`/api/bookings/${bookingId}`);
      if (!res.ok) throw new Error("Failed to fetch booking");
      return res.json();
    },
    enabled: !!bookingId,
  });

  const form = useForm<EditBookingFormValues>({
    resolver: zodResolver(editBookingSchema),
    defaultValues: {
      duration: "60",
      description: "",
      causeLinkType: "gofundme",
      causeName: "",
      causeLink: "",
      causeInstagramUrl: "",
    },
  });

  // Fetch GoFundMe links
  const { data: gofundmeLinks = [] } = useQuery({
    queryKey: ["gofundme-links"],
    queryFn: async () => {
      const res = await fetch("/api/admin/gofundme");
      if (!res.ok) throw new Error("Failed to fetch links");
      return res.json();
    },
  });

  // Update form when booking data is loaded
  useEffect(() => {
    if (booking && gofundmeLinks) {
      const startTime = new Date(booking.timeSlot.startTime);
      const endTime = new Date(booking.timeSlot.endTime);
      const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
      
      const currentCauseLink = booking.causeLink || booking.set?.causeLink;
      const currentCauseName = booking.causeName || booking.set?.causeLabel || "";
      
      console.log('Loading booking data:', {
        currentCauseLink,
        currentCauseName,
        gofundmeLinks,
        booking
      });
      
      // Check if the current cause link matches any GoFundMe link
      // Try matching by both URL and name for better accuracy
      const matchingGofundme = currentCauseLink 
        ? gofundmeLinks.find((link: any) => {
            // Normalize URLs for comparison (remove trailing slashes, etc)
            const normalizeUrl = (url: string) => {
              return url?.toLowerCase().replace(/\/$/, '').trim();
            };
            
            const urlMatch = normalizeUrl(link.link) === normalizeUrl(currentCauseLink);
            const nameMatch = currentCauseName && link.name === currentCauseName;
            
            console.log('Checking GoFundMe match:', {
              gofundmeLink: link.link,
              gofundmeName: link.name,
              currentLink: currentCauseLink,
              currentName: currentCauseName,
              urlMatch,
              nameMatch
            });
            
            return urlMatch || (nameMatch && currentCauseLink.includes('gofundme.com'));
          })
        : null;
      
      console.log('Found matching GoFundMe:', matchingGofundme);
      
      // Ensure the cause link has a protocol for validation
      let causeLink = currentCauseLink || "";
      if (causeLink && !causeLink.startsWith('http://') && !causeLink.startsWith('https://')) {
        causeLink = 'https://' + causeLink;
      }
      
      // Determine cause link type
      const hasCause = currentCauseLink || currentCauseName;
      const causeLinkType = matchingGofundme ? "gofundme" : hasCause ? "custom" : "none";

      form.reset({
        date: startTime,
        timeSlot: format(startTime, "HH:mm"),
        duration: durationMinutes === 90 ? "90" : "60",
        description: booking.notes || "",
        causeLinkType: causeLinkType as "gofundme" | "custom" | "none",
        selectedGofundmeId: matchingGofundme?.id,
        causeName: currentCauseName,
        causeLink: causeLink,
        causeInstagramUrl: booking.set?.causeInstagramUrl || "",
      });
    }
  }, [booking, gofundmeLinks, form]);

  const selectedDate = form.watch("date");
  const duration = parseInt(form.watch("duration"));
  const selectedTimeSlot = form.watch("timeSlot");

  // Fetch bookings for selected date (excluding current booking)
  const { data: bookings = [] } = useBookingsDate(selectedDate);
  const otherBookings = bookings.filter((b: any) => b.id !== parseInt(bookingId || "0"));

  // Fetch blocked dates
  const { data: blockedDates = [] } = useBlockedDates();
  
  // Fetch blocked time slots for selected date
  const { data: blockedTimeSlots = [] } = useBlockedTimeSlots(selectedDate);

  // Check if time slot is booked or blocked
  const isTimeSlotUnavailable = (time: string): { unavailable: boolean; reason?: 'booked' | 'blocked' } => {
    if (!time || !selectedDate) return { unavailable: false };

    const [hours, minutes] = time.split(":").map(Number);
    
    // Create slot start/end times as venue time (no Z suffix)
    const datePart = format(selectedDate, "yyyy-MM-dd");
    const slotStartStr = `${datePart}T${time.padStart(5, '0')}:00.000`;
    const slotEndHour = Math.floor((hours * 60 + minutes + duration) / 60);
    const slotEndMinute = (hours * 60 + minutes + duration) % 60;
    const slotEndStr = `${datePart}T${String(slotEndHour).padStart(2, '0')}:${String(slotEndMinute).padStart(2, '0')}:00.000`;

    // Check if blocked by admin
    const isBlocked = blockedTimeSlots.some((blocked: any) => {
      // Parse the blocked times - they should already be in Pacific Time
      let blockedStartStr = blocked.startTime;
      let blockedEndStr = blocked.endTime;
      
      // Remove Z suffix if present (for backwards compatibility)
      if (blockedStartStr.endsWith('Z')) {
        blockedStartStr = blockedStartStr.slice(0, -1);
      }
      if (blockedEndStr.endsWith('Z')) {
        blockedEndStr = blockedEndStr.slice(0, -1);
      }
      
      // Extract just the date and time parts for comparison
      const blockedStart = blockedStartStr.replace('T', ' ').substring(0, 19);
      const blockedEnd = blockedEndStr.replace('T', ' ').substring(0, 19);
      const slotStart = slotStartStr.replace('T', ' ').substring(0, 19);
      const slotEnd = slotEndStr.replace('T', ' ').substring(0, 19);

      // Check for overlap using string comparison
      return (
        (slotStart >= blockedStart && slotStart < blockedEnd) ||
        (slotEnd > blockedStart && slotEnd <= blockedEnd) ||
        (slotStart <= blockedStart && slotEnd >= blockedEnd)
      );
    });

    if (isBlocked) {
      return { unavailable: true, reason: 'blocked' };
    }

    // Check if booked by another user
    const isBooked = otherBookings && otherBookings.some((booking: any) => {
      // Only consider bookings with pending_approval, scheduled, or no_show status as occupied
      if (booking.lifecycleStatus !== "pending_approval" && 
          booking.lifecycleStatus !== "scheduled" && 
          booking.lifecycleStatus !== "no_show") {
        return false;
      }

      // Remove Z suffix from booking times for direct comparison
      const bookingStartStr = booking.timeSlot.startTime.replace('Z', '');
      const bookingEndStr = booking.timeSlot.endTime.replace('Z', '');

      // Check for overlap using string comparison (works because ISO format)
      return (
        (slotStartStr >= bookingStartStr && slotStartStr < bookingEndStr) ||
        (slotEndStr > bookingStartStr && slotEndStr <= bookingEndStr) ||
        (slotStartStr <= bookingStartStr && slotEndStr >= bookingEndStr)
      );
    });

    if (isBooked) {
      return { unavailable: true, reason: 'booked' };
    }

    return { unavailable: false };
  };

  // Format time slot for display
  const formatTimeSlot = (timeSlot: string): string => {
    if (!selectedDate) return timeSlot;
    
    try {
      // Parse the date and time as Pacific Time
      const dateTime = parseVenueDateTime(format(selectedDate, "yyyy-MM-dd"), timeSlot);
      // Format it for display (this will add PT indicator)
      return VenueTimeFormats.timeOnly(dateTime);
    } catch (error) {
      return timeSlot;
    }
  };

  // Update mutation
  const updateBookingMutation = useMutation({
    mutationFn: async (values: EditBookingFormValues) => {
      const response = await fetch(`/api/bookings/${bookingId}/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          date: format(values.date, "yyyy-MM-dd"),
          timeSlot: values.timeSlot,
          duration: parseInt(values.duration),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update booking");
      }

      // Also update the set information if provided
      if (booking?.set?.id) {
        const setResponse = await fetch(`/api/sets/${booking.set.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            description: values.description,
            causeName: values.causeLinkType === "none" ? undefined : values.causeName,
            causeLink: values.causeLinkType === "custom" ? values.causeLink : undefined,
            selectedGofundmeId: values.causeLinkType === "gofundme" ? values.selectedGofundmeId : undefined,
            causeInstagramUrl: values.causeLinkType === "none" ? undefined : values.causeInstagramUrl,
          }),
        });

        if (!setResponse.ok) {
          console.error("Failed to update set information");
        }
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Booking Updated",
        description: "Your booking has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      setLocation("/my-bookings");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: EditBookingFormValues) => {
    updateBookingMutation.mutate(data);
  };

  const handleCancel = () => {
    setLocation("/my-bookings");
  };

  // Date picker configuration
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentMonth = new Date();
  currentMonth.setDate(1);

  // Check if a date is blocked
  const isDateBlocked = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return blockedDates.some((blocked: any) => {
      const blockedDateStr = blocked.date.split('T')[0];
      return blockedDateStr === dateStr;
    });
  };

  const disabledDays = [
    { before: today },
    (date: Date) => {
      const day = date.getDay();
      return day === 0 || day === 6;
    },
    (date: Date) => isDateBlocked(date),
  ];

  // TODO: Remove weekday evening block when ready to open those slots
  const timeSlots = generateTimeSlots(duration).filter((slot) => {
    if (!selectedDate) return true;
    const day = selectedDate.getDay();
    const isWeekday = day >= 1 && day <= 5;
    if (!isWeekday) return true;
    const hour = parseInt(slot.split(":")[0], 10);
    const minute = parseInt(slot.split(":")[1], 10);
    return hour < 18 || (hour === 18 && minute === 0);
  });

  if (isLoadingBooking) {
    return (
      <div className="min-h-screen bg-clubhouse-black text-white flex items-center justify-center">
        <p>Loading booking details...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-clubhouse-black text-white flex items-center justify-center">
        <p>Booking not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-clubhouse-black text-white">
      <NavHeader />
      
      <main className="container mx-auto px-4 py-8 animate-page-in">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-start gap-4 pb-6 border-b border-white/10 mb-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowCancelDialog(true)}
              className="text-white/40 hover:text-white hover:bg-transparent p-0 mt-1"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold mb-2">Edit booking</h1>
              <p className="text-white/40">Update your booking details.</p>
            </div>
          </div>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Date & Time */}
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h2 className="text-lg font-semibold mb-1">Date & Time</h2>
                  <p className="text-sm text-white/40">Update when you can perform.</p>
                </div>
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <Label>Time Slot</Label>
                    <RadioGroup
                      value={form.watch("duration")}
                      onValueChange={(value) => form.setValue("duration", value as "60" | "90")}
                      className="flex gap-4 mt-2"
                    >
                      <div className="flex items-center">
                        <RadioGroupItem value="60" id="60min" />
                        <Label htmlFor="60min" className="ml-2 font-normal cursor-pointer">60 mins</Label>
                      </div>
                      <div className="flex items-center">
                        <RadioGroupItem value="90" id="90min" />
                        <Label htmlFor="90min" className="ml-2 font-normal cursor-pointer">90+ mins</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div>
                    <Label>Desired Date</Label>
                    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal mt-1",
                            !selectedDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedDate ? format(selectedDate, "EEE, MMM dd, yyyy") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => {
                            if (date) {
                              form.setValue("date", date);
                              setDatePickerOpen(false);
                            }
                          }}
                          disabled={disabledDays}
                          initialFocus
                          className=""
                        />
                      </PopoverContent>
                    </Popover>
                    {form.formState.errors.date && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.date.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="timeSlot">Desired Time</Label>
                    <Select
                      value={selectedTimeSlot}
                      onValueChange={(value) => form.setValue("timeSlot", value)}
                      disabled={!selectedDate}
                    >
                      <SelectTrigger className="mt-1" disabled={!selectedDate}>
                        <SelectValue placeholder={selectedDate ? "Select time" : "Select a date first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((slot) => {
                          const availability = isTimeSlotUnavailable(slot);
                          return (
                            <SelectItem
                              key={slot}
                              value={slot}
                              disabled={availability.unavailable}
                            >
                              {formatTimeSlot(slot)} 
                              {availability.unavailable && (
                                availability.reason === 'blocked' ? ' (Blocked)' : ' (Booked)'
                              )}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {form.formState.errors.timeSlot && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.timeSlot.message}</p>
                    )}
                    {!selectedDate && (
                      <p className="text-xs text-gray-500 mt-1">Please select a date to see available time slots</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Artist Notes */}
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h2 className="text-lg font-semibold mb-1">Artist Notes</h2>
                  <p className="text-sm text-white/40">Update your performance requirements.</p>
                </div>
                <div className="md:col-span-2">
                  <Textarea
                    {...form.register("description")}
                    placeholder="Type here..."
                    className="min-h-[100px]"
                  />
                </div>
              </div>
            </div>

            {/* Philanthropic Cause */}
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h2 className="text-lg font-semibold mb-1">Philanthropic Cause (Optional)</h2>
                  <p className="text-sm text-white/40">Your stream will promote a charity of your choice.</p>
                </div>
                <div className="md:col-span-2 space-y-4">
                  <RadioGroup
                    value={form.watch("causeLinkType")}
                    onValueChange={(value) => form.setValue("causeLinkType", value as "gofundme" | "custom" | "none")}
                    className="flex flex-col sm:flex-row gap-4"
                  >
                    <div className="flex items-center">
                      <RadioGroupItem value="gofundme" id="gofundme" />
                      <Label htmlFor="gofundme" className="ml-2 font-normal cursor-pointer">Curated from list</Label>
                    </div>
                    <div className="flex items-center">
                      <RadioGroupItem value="custom" id="custom" />
                      <Label htmlFor="custom" className="ml-2 font-normal cursor-pointer">Custom link</Label>
                    </div>
                    <div className="flex items-center">
                      <RadioGroupItem value="none" id="none" />
                      <Label htmlFor="none" className="ml-2 font-normal cursor-pointer">None</Label>
                    </div>
                  </RadioGroup>

                  {form.watch("causeLinkType") === "none" ? null : form.watch("causeLinkType") === "gofundme" ? (
                    <div>
                      <Label>Select from curated causes</Label>
                      <Select
                        value={form.watch("selectedGofundmeId")?.toString()}
                        onValueChange={(value) => {
                          const selectedLink = gofundmeLinks.find((link: any) => link.id === parseInt(value));
                          form.setValue("selectedGofundmeId", parseInt(value));
                          if (selectedLink) {
                            form.setValue("causeName", selectedLink.name);
                            // Set Instagram URL from the selected GoFundMe link
                            form.setValue("causeInstagramUrl", selectedLink.instagramUrl || "");
                          }
                        }}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select a cause" />
                        </SelectTrigger>
                        <SelectContent>
                          {gofundmeLinks.map((link: any) => (
                            <SelectItem key={link.id} value={link.id.toString()}>
                              {link.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.formState.errors.selectedGofundmeId && (
                        <p className="text-red-500 text-sm mt-1">{form.formState.errors.selectedGofundmeId.message}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label htmlFor="causeName">Name of Charity/Cause</Label>
                        <Input
                          id="causeName"
                          {...form.register("causeName")}
                          placeholder="Charity/Cause"
                          className="mt-1"
                        />
                        {form.formState.errors.causeName && (
                          <p className="text-red-500 text-sm mt-1">{form.formState.errors.causeName.message}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="causeLink">Donations Page URL</Label>
                        <Input
                          id="causeLink"
                          {...form.register("causeLink")}
                          placeholder="https://"
                          className="mt-1"
                        />
                        {form.formState.errors.causeLink && (
                          <p className="text-red-500 text-sm mt-1">{form.formState.errors.causeLink.message}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="causeInstagramUrl">Instagram Page of Charity/Cause (Optional)</Label>
                        <Input
                          id="causeInstagramUrl"
                          {...form.register("causeInstagramUrl")}
                          placeholder="https://instagram.com/CharityCause"
                          className="mt-1"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Validation Errors */}
            {Object.keys(form.formState.errors).length > 0 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded p-4 mb-4">
                <p className="text-red-500 font-semibold mb-2">Please fix the following errors:</p>
                <ul className="list-disc list-inside text-red-400">
                  {Object.entries(form.formState.errors).map(([field, error]) => (
                    <li key={field}>
                      {field}: {error?.message || "Invalid value"}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-4 pb-8">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCancelDialog(true)}
                className="border-gray-600 hover:bg-gray-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-white hover:bg-white/90 text-black"
                disabled={updateBookingMutation.isPending}
              >
                {updateBookingMutation.isPending ? "Updating..." : "Update booking"}
              </Button>
            </div>
          </form>
        </div>

        {/* Cancel Confirmation Dialog */}
        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel editing?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/40">
                Are you sure you want to cancel? All your changes will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08] text-white">
                Continue editing
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancel}
                className="bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25"
              >
                Cancel editing
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}